<template>
  <v-container fluid class="project-detail-container pa-0">

    <div class="d-flex align-center justify-end mb-0 flex-wrap" style="gap:4px;">

      <div class="d-flex align-center px-4" style="gap:4px;">

        <v-btn v-if="showResetButton"
          text small
          color="grey"
          class="px-2 text-none"
          style="font-size:12px; letter-spacing:0;"
          @click="resetLayout"
        >
          <v-icon small class="mr-1">mdi-restore</v-icon>
          Restaurar layout
        </v-btn>

        <v-btn v-if="!isMobile"
          text small
          :color="editMode ? 'green' : '#5E85EF'"
          class="px-2 text-none"
          style="font-size:12px; letter-spacing:0;"
          @click="toggleEditMode"
        >
          <v-icon small class="mr-1">{{ editMode ? 'mdi-check' : 'mdi-pencil-ruler' }}</v-icon>
          {{ editMode ? 'Guardar' : 'Editar layout' }}
        </v-btn>

        <v-menu offset-y left :close-on-content-click="false" v-model="addMenuOpen">
          <template #activator="{ on }">
            <v-btn
              text small
              color="#2D4B95"
              class="px-2 text-none"
              style="font-size:12px; letter-spacing:0;"
              v-on="on"
            >
              <v-icon small class="mr-1">mdi-view-dashboard-edit-outline</v-icon>
              Widgets
            </v-btn>
          </template>

          <v-card width="300" class="widget-menu pa-0" elevation="2">
            <div class="px-4 pt-3 pb-2">
              <p class="font-weight-bold text-primary mb-0" style="font-size:13px;">Gestionar widgets</p>
              <p class="caption text--secondary mb-0">Agrega o quita secciones del panel</p>
            </div>
            <v-divider />
            <div class="pa-2">
              <div
                v-for="w in widgetCatalog"
                :key="w.id"
                class="widget-menu-item d-flex align-center pa-2"
                :class="{ 'widget-menu-item--active': isWidgetActive(w.id) }"
              >
                <v-icon small :color="w.color" class="mr-2">{{ w.icon }}</v-icon>
                <div class="flex-grow-1">
                  <p class="mb-0 caption font-weight-medium">{{ w.label }}</p>
                  <p class="mb-0" style="font-size:10px;color:#9E9E9E;">{{ w.description }}</p>
                </div>
                <v-btn
                  icon x-small
                  :color="isWidgetActive(w.id) ? '#E53935' : '#2D4B95'"
                  @click="toggleWidget(w.id)"
                >
                  <v-icon x-small>{{ isWidgetActive(w.id) ? 'mdi-minus-circle' : 'mdi-plus-circle' }}</v-icon>
                </v-btn>
              </div>
            </div>
            <v-divider />
            <div class="d-flex justify-space-between pa-2">
              <v-btn text x-small color="#9E9E9E" @click="resetLayout">
                <v-icon x-small left>mdi-restore</v-icon>Restablecer
              </v-btn>
              <v-btn text x-small color="#2D4B95" @click="addMenuOpen = false">Cerrar</v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>

    <v-expand-transition>
      <div v-if="editMode && !isMobile" class="edit-mode-banner d-flex align-center mb-3 px-3 py-2">
        <v-icon small color="#5E85EF" class="mr-2">mdi-information-outline</v-icon>
        <span class="caption" style="color:#3a5bc7;">
          Arrastra los widgets desde la barra azul. Redimensiona desde la esquina inferior derecha. Haz clic en
          <strong>Guardar layout</strong> cuando termines.
        </span>
      </div>
    </v-expand-transition>

    <!-- Mobile: simple vertical stack -->
    <div v-if="layout.length > 0 && isMobile" class="mobile-stack">
      <div v-for="item in layout" :key="item.i" class="grid-item-wrapper grid-item-mobile">
        <div class="widget-content">
          <ProjectStatusCard v-if="item.i === 'status'" />
          <WeatherWidget v-else-if="item.i === 'weather'" />
          <WeatherWidgetVertical v-else-if="item.i === 'weather-vertical'" />
          <ProjectDetailCard v-else-if="item.i === 'tasks'" type="task" :data="dashboardInfo ? dashboardInfo.task : null" @goToListing="goToListing('tasks')"/>
          <ProjectDetailCard v-else-if="item.i === 'rdi'" type="rdi" :data="dashboardInfo ? dashboardInfo.rdi : null" @goToListing="goToListing('rdi')"/>
          <ProjectDetailCard v-else-if="item.i === 'additional'" type="additional" :data="dashboardInfo ? dashboardInfo.additional : null" @goToListing="goToListing('additional')"/>
          <ForgeViewer v-else-if="item.i === 'forge-viewer'" :selectedFile="forgeSelectedFile" @file-selected="onForgeFileSelected" />
          <ProgressChartWidget v-else-if="item.i === 'progress-chart'" />
        </div>
      </div>
    </div>

    <!-- Desktop / tablet: original grid layout -->
    <grid-layout
      v-else
      :layout.sync="layout"
      :col-num="12"
      :row-height="80"
      :is-draggable="editMode && !isMobile"
      :is-resizable="editMode && !isMobile"
      :margin="[12, 12]"
      :use-css-transforms="true"
      :vertical-compact="true"
      :auto-size="true"
      @layout-updated="onLayoutUpdated"
    >
      <grid-item
        v-for="item in layout"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
        :min-w="item.minW || 2"
        :min-h="item.minH || 2"
        drag-allow-from=".widget-drag-handle"
        class="grid-item-wrapper"
        :class="{ 'grid-item-wrapper--editing': editMode }"
      >
        <div v-if="editMode" class="widget-edit-bar d-flex align-center justify-space-between px-2">
          <div class="d-flex align-center" style="gap:6px;">
            <v-icon class="widget-drag-handle" small color="white">mdi-drag</v-icon>
            <span class="white--text" style="font-size:11px;font-weight:500;">{{ labelFor(item.i) }}</span>
          </div>
          <v-btn icon x-small @click="removeWidget(item.i)">
            <v-icon x-small color="rgba(255,255,255,0.75)">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="widget-content" :class="{ 'widget-content--editing': editMode }">
          <ProjectStatusCard    v-if="item.i === 'status'" />
          <WeatherWidget        v-else-if="item.i === 'weather'" />
          <WeatherWidgetVertical        v-else-if="item.i === 'weather-vertical'" />
          <ProjectDetailCard    v-else-if="item.i === 'tasks'"      type="task"       :data="dashboardInfo ? dashboardInfo.task       : null" @goToListing="goToListing('tasks')"/>
          <ProjectDetailCard    v-else-if="item.i === 'rdi'"        type="rdi"        :data="dashboardInfo ? dashboardInfo.rdi        : null" @goToListing="goToListing('rdi')"/>
          <ProjectDetailCard    v-else-if="item.i === 'additional'" type="additional" :data="dashboardInfo ? dashboardInfo.additional : null" @goToListing="goToListing('additional')"/>
          <ForgeViewer    v-else-if="item.i === 'forge-viewer'" :selectedFile="forgeSelectedFile" @file-selected="onForgeFileSelected" />
          <ProgressChartWidget v-else-if="item.i === 'progress-chart'" />
        </div>
      </grid-item>
    </grid-layout>

    <div v-if="layout.length === 0" class="empty-panel text-center py-12">
      <v-icon size="52" color="#BDBDBD">mdi-view-dashboard-outline</v-icon>
      <p class="text--secondary mt-3 mb-1 font-weight-medium">El panel está vacío</p>
      <p class="caption text--secondary mb-4">Agrega widgets desde el menú "Widgets"</p>
      <v-btn rounded small outlined color="#2D4B95" @click="addMenuOpen = true">
        <v-icon small left>mdi-plus</v-icon>Agregar widgets
      </v-btn>
    </div>

  </v-container>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout'
import ProjectDetailCard from '@/components/Views/Home/ProjectDetails/Widgets/ProjectDetailCard'
import ProjectStatusCard from '@/components/Views/Home/ProjectDetails/Widgets/ProjectStatusCard'
import WeatherWidget from '@/components/Views/Home/ProjectDetails/Widgets/WeatherWidget'
import WeatherWidgetVertical from '@/components/Views/Home/ProjectDetails/Widgets/WeatherWidgetVertical'
import ForgeViewer from '@/components/Views/Home/ProjectDetails/Widgets/ForgeViewer'
import ProgressChartWidget from '@/components/Views/Home/ProjectDetails/Widgets/ProgressChartWidget'
import Chart from 'chart.js'
import EventBus from '@/helpers/event_bus'

const WIDGET_CATALOG = [
  {
    id: 'status',
    label: 'Estado del proyecto',
    description: 'Imagen, almacenamiento y tablero',
    icon: 'mdi-clipboard-text-outline',
    color: '#2D4B95',
    default: { x: 0, y: 0, w: 3, h: 5, minW: 2, minH: 3 }
  },
  {
    id: 'weather',
    label: 'Clima',
    description: 'Temperatura y pronóstico en la ubicación del proyecto',
    icon: 'mdi-weather-partly-cloudy',
    color: '#378ADD',
    default: { x: 3, y: 0, w: 3, h: 5, minW: 2, minH: 3 }
  },
  {
    id: 'weather-vertical',
    label: 'Clima [Vertical]',
    description: 'Temperatura y pronóstico en la ubicación del proyecto',
    icon: 'mdi-weather-partly-cloudy',
    color: '#378ADD',
    default: { x: 3, y: 0, w: 3, h: 5, minW: 2, minH: 3 }
  },
  {
    id: 'tasks',
    label: 'Tareas',
    description: 'Resumen de tareas del proyecto',
    icon: 'mdi-checkbox-marked-circle-outline',
    color: '#1D9E75',
    default: { x: 0, y: 12, w: 6, h: 4, minW: 3, minH: 3 }
  },
  {
    id: 'rdi',
    label: 'RDIs',
    description: 'Solicitudes de información',
    icon: 'mdi-chat-question-outline',
    color: '#D85A30',
    default: { x: 0, y: 12, w: 6, h: 4, minW: 3, minH: 3 }
  },
  {
    id: 'additional',
    label: 'Adicionales',
    description: 'Trabajos y cobros adicionales',
    icon: 'mdi-plus-circle-outline',
    color: '#7F77DD',
    default: { x: 0, y: 12, w: 6, h: 4, minW: 3, minH: 3 }
  },
  {
    id: 'forge-viewer',
    label: 'Visor Autodesk',
    description: 'Visualización de modelos Autodesk Forge',
    icon: 'mdi-cube-outline',
    color: '#FF6F00',
    default: { x: 0, y: 12, w: 6, h: 4, minW: 3, minH: 3 }
  },
  {
    id: 'progress-chart',
    label: 'Control de Avance',
    description: 'Gráfico de avance planificado vs real',
    icon: 'mdi-chart-line',
    color: '#2D4B95',
    default: { x: 0, y: 12, w: 6, h: 4, minW: 3, minH: 3 }
  }
]

const STORAGE_KEY = 'projectDetailLayout_v2'

export default {
  name: 'ProjectDetail',

  components: {
    GridLayout,
    GridItem,
    ProjectDetailCard,
    ProjectStatusCard,
    WeatherWidget,
    WeatherWidgetVertical,
    ForgeViewer,
    ProgressChartWidget
  },

  data: () => ({
    layout: [],
    editMode: false,
    addMenuOpen: false,
    forgeSelectedFile: null,
    // fallback width for environments without $vuetify.breakpoint
    windowWidth: (typeof window !== 'undefined') ? window.innerWidth : 1200
  }),

  computed: {
		dashboardInfo(){
			return this.$store.projectsStore.state.dashboardInfo
		},
    selectedProject () {
      return this.$store.projectsStore.state.selectedProject
    },
    widgetCatalog () {
      return WIDGET_CATALOG
    },
		defaultLayout () {
			return [{"i":"status","x":0,"y":4,"w":3,"h":4,"minW":2,"minH":3,"moved":false},{"i":"weather-vertical","x":3,"y":4,"w":3,"h":4,"minW":2,"minH":3,"moved":false},{"i":"forge-viewer","x":6,"y":0,"w":6,"h":4,"minW":6,"minH":4,"moved":false},{"i":"progress-chart","x":0,"y":0,"w":6,"h":4,"minW":3,"minH":3,"moved":false},{"i":"tasks","x":6,"y":4,"w":6,"h":4,"minW":3,"minH":3,"moved":false},{"i":"rdi","x":0,"y":8,"w":6,"h":4,"minW":3,"minH":3,"moved":false},{"i":"additional","x":6,"y":8,"w":6,"h":4,"minW":3,"minH":3,"moved":false}]
		},
		showResetButton() {
			const defaultLayout = JSON.stringify(this.defaultLayout.map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH })))
			const currentLayout = JSON.stringify(this.layout.map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH })))
			return defaultLayout !== currentLayout
		},
    isMobile () {
      // Prefer Vuetify breakpoint if available (reactive), otherwise use window width
      if (this.$vuetify && this.$vuetify.breakpoint) return this.$vuetify.breakpoint.smAndDown
      return this.windowWidth <= 600
    }
  },

  watch: {
    selectedProject (newVal) {
      if (newVal?.id) this.getInfoForDashboard()
    }
    ,
    isMobile (newVal) {
      // When switching to mobile, ensure edit mode is disabled
      if (newVal && this.editMode) this.editMode = false
    }
  },

  mounted () {
    this.loadLayout()
    this.registerChartPlugin()
    this.getInfoForDashboard()
    // Inicializar archivo seleccionado de Forge
    this.forgeSelectedFile = this.$store.forgeStore.state.forgeSelectedFile
    // keep a fallback reactive width if Vuetify breakpoint isn't available
    window.addEventListener('resize', this.onResize)
		EventBus.$on('onProjectElementsChange', this.getInfoForDashboard)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
		EventBus.$off('onProjectElementsChange', this.getInfoForDashboard)
  },

  methods: {
    onResize () {
      this.windowWidth = window.innerWidth
    },
    // ── Layout persistence ─────────────────────────────────────────────────

    loadLayout () {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          this.layout = JSON.parse(saved)
        } else {
          this.layout = this.buildDefaultLayout()
        }
      } catch {
        this.layout = this.buildDefaultLayout()
      }
    },

    buildDefaultLayout () {
      return this.defaultLayout
    },

    saveLayout () {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.layout))
    },

    resetLayout () {
      this.layout = this.buildDefaultLayout()
      this.saveLayout()
    },

    onLayoutUpdated (newLayout) {
      // vue-grid-layout emits the full updated array after any drag/resize
      this.layout = newLayout
      this.saveLayout()
    },

    // ── Edit mode ──────────────────────────────────────────────────────────

    toggleEditMode () {
      this.editMode = !this.editMode
      if (!this.editMode) this.saveLayout()
    },

    // ── Widget management ──────────────────────────────────────────────────

    isWidgetActive (id) {
      return this.layout.some(item => item.i === id)
    },

    toggleWidget (id) {
      if (this.isWidgetActive(id)) {
        this.removeWidget(id)
      } else {
        this.addWidget(id)
      }
    },

    addWidget (id) {
      const catalog = WIDGET_CATALOG.find(w => w.id === id)
      if (!catalog) return
      // Stack below existing items
      const maxY = this.layout.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)
      this.layout.push({
        i: id,
        x: 0,
        y: maxY,
        w: catalog.default.w,
        h: catalog.default.h,
        minW: catalog.default.minW,
        minH: catalog.default.minH
      })
      this.saveLayout()
    },

    removeWidget (id) {
      this.layout = this.layout.filter(item => item.i !== id)
      this.saveLayout()
    },

    labelFor (id) {
      return WIDGET_CATALOG.find(w => w.id === id)?.label || id
    },

    // ── Dashboard data ─────────────────────────────────────────────────────

    async getInfoForDashboard () {
			this.$store.projectsStore.dispatch('getDashboardInfo')
    },

    // ── Chart.js center-text plugin ────────────────────────────────────────

    registerChartPlugin () {
      Chart.pluginService.register({
        beforeDraw (chart) {
          const { width, height, ctx } = chart.chart
          ctx.restore()
          const fontSize = (height / 50).toFixed(2)
          ctx.font = fontSize + 'em sans-serif'
          ctx.textBaseline = 'middle'
          const text = chart.config.options?.elements?.center?.text || ''
          const textX = Math.round((width - ctx.measureText(text).width) / 2)
          ctx.fillText(text, textX, height / 1.8)
          ctx.save()
        }
      })
    },

    // ── Forge Viewer ───────────────────────────────────────────────────────

    onForgeFileSelected (file) {
      this.forgeSelectedFile = file
      this.$store.forgeStore.commit('setForgeSelectedFile', file)
    },

		// ── Navigation from cards ─────────────────────────────────────────────

		goToListing (type) {
			if (type === 'tasks') {
				this.$router.push({ name: 'Consultas' })
			} else if (type === 'rdi') {
				this.$router.push({ name: 'Rdi' })
			} else if (type === 'additional') {
				this.$router.push({ name: 'Adicionales' })
			}
		},
  }
}
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────────────────── */
.project-detail-container { min-height: 400px; }
.text-primary { color: #2D4B95; }

/* ── Edit mode banner ──────────────────────────────────────────────── */
.edit-mode-banner {
  background: #EEF2FF;
  border: 1px solid #C5CFF7;
  border-radius: 8px;
}

/* ── Widget manager menu ───────────────────────────────────────────── */
.widget-menu { border-radius: 12px !important; }

.widget-menu-item {
  border-radius: 8px;
  transition: background 0.12s;
}
.widget-menu-item:hover       { background: #F5F7FF; }
.widget-menu-item--active     { background: #F0F4FF; }

/* ── Grid item wrapper ─────────────────────────────────────────────── */
.grid-item-wrapper {
  border-radius: 12px;
  overflow: hidden;
  background: var(--v-theme-surface, #fff);
  border: 0.5px solid rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
}
.grid-item-wrapper--editing {
  box-shadow: 0 0 0 2px #5E85EF;
}

/* Edit bar */
.widget-edit-bar {
  background: #5E85EF;
  height: 28px;
  flex-shrink: 0;
  cursor: move;
}
.widget-drag-handle         { cursor: grab; }
.widget-drag-handle:active  { cursor: grabbing; }

/* Widget content fills remaining height */
.widget-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
/* Make child components fill the cell */
.widget-content > * { height: 100%; }

/* ── Empty state ───────────────────────────────────────────────────── */
.empty-panel {
  border: 2px dashed #E0E0E0;
  border-radius: 12px;
}

/* ── vue-grid-layout overrides ─────────────────────────────────────── */

/* Drop placeholder */
:deep(.vue-grid-placeholder) {
  background: #EEF2FF !important;
  border: 2px dashed #5E85EF !important;
  border-radius: 12px !important;
  opacity: 1 !important;
}

/* Resize handle — small L-bracket in corner */
:deep(.vue-resizable-handle) {
  background-image: none !important;
  width: 18px !important;
  height: 18px !important;
  bottom: 5px !important;
  right: 5px !important;
  border-bottom: 2.5px solid #5E85EF;
  border-right: 2.5px solid #5E85EF;
  border-radius: 0 0 6px 0;
  opacity: 0;
  transition: opacity 0.15s;
}
.grid-item-wrapper--editing :deep(.vue-resizable-handle) {
  opacity: 0.5;
}
.grid-item-wrapper--editing:hover :deep(.vue-resizable-handle) {
  opacity: 1;
}

/* Item being dragged */
:deep(.vue-grid-item.vue-draggable-dragging) {
  opacity: 0.88;
  box-shadow: 0 16px 40px rgba(93,133,239,0.22) !important;
  z-index: 100;
}

/* Smooth reflow for non-dragging items */
:deep(.vue-grid-item:not(.vue-draggable-dragging):not(.resizing)) {
  transition: all 0.18s ease;
}

/* Mobile stack tweaks */
.mobile-stack .grid-item-wrapper {
  width: 100%;
  margin-bottom: 12px;
}
.grid-item-mobile .widget-content > * { height: auto; }
</style>